/* Iter 103.0 · Phase 1 — Nav Registry Extraction
   ────────────────────────────────────────────────
   Canonical source of truth for the sidebar navigation tree and its
   visibility helpers. Extracted verbatim from `Sidebar.jsx` (Iter 102
   Menu IA v3 Stage 2A) so the same registry can be consumed by:

     • `Sidebar.jsx`          — renders the navigation UI.
     • `GlobalSearchBar.jsx`  — indexes nav items for menu search
                                (Iter 103.0 Phase 2).

   ADR (2026-02-11): One registry, one visibility gate. Any second
   copy of NAV_GROUPS would silently drift from the sidebar; the
   platform has multiple prior incidents of this exact drift pattern
   (permission-map + sidebar diverging from route table), so we
   collapse this to a single import.

   Phase 1 invariant: This file is a pure lift-and-shift from
   Sidebar.jsx lines 51-60 (icon imports), 75-126 (visibility
   helpers), 131-682 (NAV_GROUPS + DASHBOARD_ITEM), and 700-718
   (findOwnersForPath / findGroupForPath). No structural changes.
   No new fields. Every icon reference, permission string, and
   comment carried across as-is. Zero user-visible behaviour change.
*/

import {
    SquaresFour, Users, FileText, Receipt, HandCoins,
    ShieldCheck, UsersThree, FileDoc, Repeat, Stack, Scales,
    ArrowsClockwise, ChartBar, ChartLineUp, Funnel, Target,
    Envelope, Package, Warning, Database, Bank, Briefcase,
    Buildings, ChartPieSlice, MagicWand,
    Wrench, Handshake, AddressBook,
    IdentificationCard, CalendarPlus, Calendar, Clock,
    Coins, ListChecks, Money, Calculator, Lock, Lightning,
    UserMinus, Desktop, Plug, Pulse, Gavel,
    ClipboardText, CheckCircle, Lightbulb, Shield,
} from "@phosphor-icons/react";

// ── Visibility helpers ─────────────────────────────────────────────
// Each item declares ONE of these gates: `perm` (single permission key
// checked via auth.can()), `permAny: [...]` (OR-gate — visible if any
// listed permission is held), `role: "super_admin"`, `primary: true`,
// or `linkedEmployee: true` (Iter 45.6.2 — the item renders iff the
// caller has a linked employee record; used for the 5 mandatory ESS
// menus that are visibility-by-identity rather than by-role-grant).
// `hideForRoles: [...]` is an *additional* filter that suppresses the
// item even if the gate passes — used to keep entire modules out
// of narrow personas (e.g. DATA_SALES sees Dashboard + Leads via
// sales:read but must NOT see Proposals / Customer POs per the
// "responsibility ends at lead generation" directive). `isItemVisible`
// returns true iff the gate passes for the current user. Items with
// no gate are visible to everyone authenticated.
export function isItemVisible(item, user, can) {
    if (item.hideForRoles && item.hideForRoles.includes(user?.role)) return false;
    if (item.primary) return !!user?.is_primary_admin;
    if (item.role === "super_admin") return user?.role === "super_admin";
    if (item.permAny && item.permAny.length) return item.permAny.some((p) => can(p));
    if (item.perm) return can(item.perm);
    // Iter 45.6.2 (2026-07-14) — Employee-linked dynamic gate.
    if (item.linkedEmployee) return !!user?.is_employee_linked;
    return true;
}

// ── Display-time group resolution ──────────────────────────────────
// UAT refinement (2026-02): for non-admin / self-service / manager
// personas, two group labels are reframed at render time. NAV_GROUPS
// itself is NOT mutated (Menu IA v2 guard still passes — it checks
// the literal `id: "..."` / `label: "..."` pairs in source). These
// overrides apply only to what the user sees in the rendered DOM.
//
// 1. Employees → My Workspace
//    When the user has no visible root items (Directory / Onboard
//    Employee/User) AND only the "my-workspace" ESS subgroup is
//    visible, the group is re-labelled "My Workspace" and the
//    subgroup's items are promoted to root level so the user does
//    not see "My Workspace" twice (parent + subgroup).
//
// 2. Workforce → Team Insights
//    When the user is NOT HR/admin (i.e., lacks workforce:read AND
//    employee:onboard AND role:write), the group is re-labelled
//    "Team Insights". The inner "team-insights" subgroup name is
//    kept (small grey eyebrow style) — visual collision is minimal
//    and the cluster's meaning is preserved.
export function resolveGroupDisplay(group, visibleItems, visibleSubgroups, can) {
    // 1 — Employees → My Workspace
    if (
        group.id === "employees"
        && visibleItems.length === 0
        && visibleSubgroups.length === 1
        && visibleSubgroups[0].id === "my-workspace"
    ) {
        return {
            label: "My Workspace",
            visibleItems: visibleSubgroups[0].items,
            visibleSubgroups: [],
        };
    }
    // 2 — Workforce → Team Insights (manager / non-admin)
    if (group.id === "workforce") {
        const isAdminOrHr = can("workforce:read") || can("employee:onboard") || can("role:write");
        if (!isAdminOrHr) {
            return { label: "Team Insights", visibleItems, visibleSubgroups };
        }
    }
    return { label: group.label, visibleItems, visibleSubgroups };
}

// ── Dashboard (root-level entry — NOT inside any group) ───────────
// Rendered above the group accordion. Kept out of NAV_GROUPS so the
// menu IA guard tests can assert /dashboard is a Level-1 entry.
// Iter 107 · Phase 1 — T-1 Dashboard Gate fix (per frozen Persona
// Footprint · Role & Navigation Hardening). Expanded from the legacy
// sales/finance duo to also admit repository, HR, and compliance
// personas — unblocks hr_user · data_user · logo_user · developer ·
// operations_user at first-login. `employee:read` also admits any
// employee-linked user; `compliance:read` admits the compliance
// persona (§2.17 target-state). Persona-specific landing surfaces
// are Phase 2; Phase 1 lands everyone on the generic `/dashboard`.
export const DASHBOARD_ITEM = {
    to: "/dashboard",
    label: "Dashboard",
    icon: SquaresFour,
    permAny: ["sales:read", "finance:read", "data:read", "employee:read", "compliance:read"],
};

// ── Navigation tree — Menu IA v3 (Stage 1 restructure) ────────────
// Locked order per NAVIGATION_IMPACT_MATRIX.md §B:
//   Sales · Accounts · Vendors · Workforce · Employees ·
//   Payroll · Data Hub · Administration · Platform Setup
// Stage 1 rules (Option (b) §I): reorganise already-linked items
// only. Orphans stay orphan; no URL / permission / backend changes.
export const NAV_GROUPS = [
    // 1 — SALES (CRM · Proposal Management · Delivery)
    {
        id: "sales",
        label: "Sales",
        icon: Briefcase,
        accent: "blue",
        items: [],
        subgroups: [
            {
                id: "sa-crm",
                label: "CRM",
                icon: UsersThree,
                items: [
                    { to: "/leads", label: "Leads", icon: UsersThree, perm: "sales:read" },
                    { to: "/clients", label: "Clients", icon: Users, perm: "read" },
                    // Iter 102.1 Nav Cleanup — Pipeline Analytics MOVED from
                    // Accounts → Reports to Sales → CRM per operator directive.
                    // Rationale: pipeline is a Sales/BD concept, not Finance.
                    // Route permission updated to OR-gate sales:read | finance:read
                    // in App.js so both persona groups retain access.
                    //
                    // Iter 107 · Phase 1 — T-5 Sales cleanup (frozen Decision
                    // Log v2): Pipeline Analytics is a Sales-Manager surface,
                    // not a Sales-Rep surface. `sales` role hidden here.
                    // Viewer keeps access in Phase 1 (Pipeline Snapshot MVP
                    // is a Phase 2 deliverable; removing this from Viewer
                    // now would regress 141 users — see Persona Footprint).
                    { to: "/reports/pipeline", label: "Pipeline Analytics", icon: Funnel, perm: "sales:read", hideForRoles: ["sales", "data_sales"] },
                ],
            },
            {
                id: "sa-proposal-mgmt",
                label: "Proposal Management",
                icon: FileDoc,
                items: [
                    { to: "/proposals", label: "Proposal Builder", icon: FileDoc, perm: "sales:read", hideForRoles: ["data_sales"] },
                    // 2026-02 · NDA Builder Phase 1 — Sales sibling of
                    // Proposal Builder.  Gated on ``nda:read`` (grants
                    // to super_admin + sales via idempotent boot
                    // migration).
                    { to: "/ndas", label: "NDAs", icon: Shield, perm: "nda:read", hideForRoles: ["data_sales"] },
                    // F.8 multi-entry — also linked under Platform Setup → Templates.
                    // Navigation policy (2026-07-14): Product Catalogue is an admin-only
                    // menu. Proposal Builder continues to consume catalogue data via API
                    // regardless — this hides only the standalone entry from Sales /
                    // Viewer / other normal roles. Route guard in App.js is unchanged.
                    { to: "/catalogue", label: "Product Catalogue", icon: Package, role: "super_admin" },
                    { to: "/terms-conditions", label: "Terms & Conditions", icon: Scales, perm: "template:write" },
                ],
            },
            {
                id: "sa-delivery",
                label: "Delivery",
                icon: Briefcase,
                items: [
                    { to: "/purchase-orders", label: "Customer POs", icon: FileText, perm: "sales:read", hideForRoles: ["data_sales"] },
                    { to: "/projects", label: "Projects", icon: Briefcase, perm: "project:read", hideForRoles: ["data_sales"] },
                    // W3-A · B7 — Project Initiation Workflow.
                    // Visible only to holders of the workflow read/approve
                    // capabilities. Empty / unauthorised entries are
                    // suppressed automatically by `isItemVisible`.
                    { to: "/workflows/my-requests", label: "My Project Requests", icon: ClipboardText, perm: "workflow:read_own" },
                    { to: "/workflows/approvals", label: "Project Approvals", icon: CheckCircle, perm: "project:approve" },
                    // Navigation policy (2026-07-14): "Unassigned AMCs" menu removed.
                    // The label was misleading ("unassigned" meant "not linked to a
                    // Project", not "no client/owner"). The Project ↔ AMC linkage
                    // requires a separate business review — 92.8% of preview AMCs
                    // are currently not project-linked and Projects have not been
                    // backfilled. Backend endpoints (/orphan-amc-queue, attach-amc,
                    // detach-amc, orphan-amc-export), `project_id` field, the
                    // `OrphanAmcQueue.jsx` component, and its tests are preserved.
                    // The legacy route `/projects/orphan-amcs` redirects to
                    // `/projects` (see App.js).
                ],
            },
        ],
    },
    // 2 — ACCOUNTS (Operations · Reports)
    {
        id: "accounts",
        label: "Accounts",
        icon: Bank,
        accent: "emerald",
        items: [],
        subgroups: [
            {
                id: "ac-ops",
                label: "Operations",
                icon: HandCoins,
                items: [
                    { to: "/invoices", label: "Invoices", icon: Receipt, perm: "finance:read" },
                    { to: "/payments", label: "Payments", icon: HandCoins, perm: "finance:read" },
                    { to: "/credit-notes", label: "Credit Notes", icon: ArrowsClockwise, perm: "finance:read" },
                    { to: "/amc", label: "AMC", icon: Repeat, perm: "finance:read" },
                    { to: "/bank-transactions", label: "Bank Transactions", icon: Bank, perm: "bank_txn:read" },
                ],
            },
            {
                // Interim flat links per operator directive (Stage 1 answer #2).
                // Stage 2A · FPR-003/004/005 add stale-renewals + customer
                // compliance (GST, Reconciliation) as siblings. Stage 3 will
                // resolve whether Compliance Reports / GST / Reconciliation
                // consolidate into a compliance tab-parent.
                id: "ac-reports",
                label: "Reports",
                icon: ChartBar,
                items: [
                    // Iter 102.1 Nav Cleanup — labels business-friendly-ised.
                    // "Collections Priority" → "Priority Collections" (natural order).
                    { to: "/reports/collections", label: "Priority Collections", icon: Target, perm: "finance:read" },
                    { to: "/reports/aging", label: "Aging Report", icon: ChartBar, perm: "finance:read" },
                    { to: "/reports/cashflow-forecast", label: "Cashflow Forecast", icon: ChartLineUp, perm: "finance:read" },
                    // Pipeline Analytics MOVED to Sales → CRM in Iter 102.1.
                    // "Compliance Reports" → "Customer TDS" (completes Customer <X> trio).
                    { to: "/reports/compliance/tds", label: "Customer TDS", icon: ShieldCheck, perm: "compliance:read" },
                    // "Stale Renewals" → "Overdue Renewals" (business-friendly).
                    { to: "/reports/stale-renewals", label: "Overdue Renewals", icon: Warning, perm: "finance:read" },
                    // Stage 2A · FPR-004 — Statutory customer-side GST report.
                    { to: "/reports/compliance/gst", label: "Customer GST", icon: ShieldCheck, perm: "compliance:read" },
                    // Stage 2A · FPR-005 — Customer reconciliation.
                    { to: "/reports/compliance/reconciliation", label: "Customer Reconciliation", icon: ArrowsClockwise, perm: "compliance:read" },
                ],
            },
        ],
    },
    // 3 — VENDORS (Master · AP Ops)
    {
        id: "vendors",
        label: "Vendors",
        icon: Handshake,
        accent: "purple",
        items: [],
        subgroups: [
            {
                // Iter 102.1 Nav Cleanup — L2 "Master" → "Vendor Directory";
                // L3 "Vendor Master" → "Directory" (parallels Employees).
                id: "vd-master",
                label: "Vendor Directory",
                icon: AddressBook,
                items: [
                    { to: "/vendors", label: "Directory", icon: AddressBook, perm: "vendor:read" },
                ],
            },
            {
                // Iter 102.1 — L2 "AP Ops" → "Payables"; L3 items prefixed
                // "Vendor" for consistency + disambiguation vs Sales POs/Payments.
                id: "vd-ap-ops",
                label: "Payables",
                icon: HandCoins,
                items: [
                    { to: "/vendor-quotations", label: "Vendor Quotations", icon: Receipt, perm: "vendor:read" },
                    { to: "/vendor-purchase-orders", label: "Vendor POs", icon: FileText, perm: "vendor:read" },
                    { to: "/vendor-bills", label: "Vendor Bills", icon: Receipt, perm: "vendor:read" },
                    { to: "/vendor-payments", label: "Vendor Payments", icon: HandCoins, perm: "vendor:read" },
                ],
            },
            // Stage 2A · FPR-006/007/008 — New Reports L2 for vendor-side
            // statutory reporting. Approved by operator directive 2026-02.
            // Compliance-gated (not vendor:read) because these are
            // statutory reports consumed by Finance / Compliance, not by
            // vendor CRUD operators.
            {
                id: "vd-reports",
                label: "Reports",
                icon: ChartBar,
                items: [
                    { to: "/reports/vendor-compliance/tds", label: "Vendor TDS", icon: ShieldCheck, perm: "compliance:read" },
                    { to: "/reports/vendor-compliance/gst", label: "Vendor GST", icon: ShieldCheck, perm: "compliance:read" },
                    { to: "/reports/vendor-compliance/input-gst", label: "Vendor Input GST", icon: ShieldCheck, perm: "compliance:read" },
                ],
            },
        ],
    },
    // 4 — WORKFORCE (Attendance · Leave Ops · Leave Admin · Insights · Reports · Agents)
    {
        id: "workforce",
        label: "Workforce",
        icon: UsersThree,
        accent: "teal",
        items: [],
        subgroups: [
            {
                // Iter 102.1 Nav Cleanup — L3 labels business-friendly-ised
                // per operator directive:
                //   "Anomalies Inbox"        → "Attendance Exceptions"
                //   "Attendance Workspace"   → "Manager Attendance View"
                //   "HR Dashboard"           → "HR Attendance Dashboard"
                //   "Policies"               → "Attendance Policies"
                //   "Assignments"            → "Policy Assignments"
                id: "wf-attendance",
                label: "Attendance",
                icon: Clock,
                items: [
                    { to: "/attendance/anomalies", label: "Attendance Exceptions", icon: Warning, perm: "employee:read" },
                    // Iter 45.6.1 (2026-07-14) · Phase 1 · Manager Nav Gate
                    // Relaxation. `/regularization/requests` and
                    // `/attendance/workspace` are manager-approval
                    // surfaces. Backend approve/reject is identity-based
                    // (approver_chain check), not RBAC-based — pure
                    // Managers (holding `manager:read` via Class-C
                    // identity grant but NOT `employee:read`) must
                    // reach these surfaces. HR retains access via
                    // `employee:read`. See PRD Iter 45.6 §M.
                    { to: "/regularization/requests", label: "Regularizations", icon: FileText, permAny: ["employee:read", "manager:read"] },
                    { to: "/admin/attendance/month-close", label: "Attendance Month Close", icon: CheckCircle, permAny: ["employee:admin", "payroll:admin"] },
                    { to: "/attendance/workspace", label: "Manager Attendance View", icon: UsersThree, permAny: ["employee:read", "manager:read"] },
                    { to: "/admin/attendance/hr-dashboard", label: "HR Attendance Dashboard", icon: ChartLineUp, perm: "employee:read" },
                    { to: "/admin/attendance/shifts", label: "Shifts", icon: Clock, perm: "employee:read" },
                    { to: "/admin/attendance/policies", label: "Attendance Policies", icon: Scales, perm: "employee:read" },
                    { to: "/admin/attendance/assignments", label: "Policy Assignments", icon: AddressBook, perm: "employee:read" },
                    // 2026-08-05 · Bulk Regularize — back-fill paired
                    // check-in/check-out events for a date range for
                    // employees who cannot self-serve (no computer
                    // access, extended field trips, etc).  Gated on
                    // employee:admin.
                    { to: "/admin/attendance/bulk-regularize", label: "Bulk Regularize", icon: MagicWand, perm: "employee:admin" },
                    // P1-A (2026-02) · Manual Bulk Attendance Upload.
                    // Endpoint-layer perms guard commit / overwrite /
                    // rollback; the nav entry just checks the read gate.
                    { to: "/attendance/bulk-upload", label: "Bulk Attendance Upload", icon: MagicWand, perm: "attendance:bulk_upload:read" },
                    // P1-B (2026-02) · Attendance Source Configuration —
                    // bulk-admin surface for desktop_agent / hybrid mode
                    // overrides. Endpoint-layer perm is checked on every
                    // call; nav only checks the read gate.
                    { to: "/admin/attendance/source-config", label: "Attendance Source Config", icon: Scales, perm: "attendance:source_config:manage" },
                    // P1-C (2026-02) · Biometric Attendance console —
                    // vendor-neutral device master, identity mappings,
                    // bulk mapping import (dry-run → commit), unmapped
                    // events inbox (explicit resolve/reject). Ingest is
                    // authenticated per-device and does not require this
                    // capability.
                    { to: "/admin/attendance/biometric", label: "Biometric Console", icon: Scales, perm: "biometric:device:manage" },
                ],
            },
            {
                // Iter 102.1 Nav Cleanup — L2 "Leave Ops" → "Leave Operations"
                // (spell out abbreviation).
                //
                // Iter 107 · Phase 1 — T-4 HR route elevation (frozen Decision
                // Log v3): "Manager Leave Workspace" promoted from HIDDEN_ROUTES
                // to first-class sidebar. Every route now has a persona owner
                // (Constitution §GP-8).
                id: "wf-leave-ops",
                label: "Leave Operations",
                icon: CalendarPlus,
                items: [
                    // Stage 1 rename: "Approval Dashboard" → "Leave Dashboard".
                    // Iter 45.6.1 (2026-07-14) · Phase 1 · Manager Nav Gate
                    // Relaxation — Leave Ops manager surfaces are
                    // identity-approver based at the backend; expose to
                    // pure Managers via permAny. Phase 2 (Iter 45.7)
                    // will retire `/leave/workspace` and
                    // `/leave/approval-dashboard` in favour of a
                    // unified "My Team → Approval Inbox".
                    { to: "/leave/approval-dashboard", label: "Leave Dashboard", icon: ChartLineUp, permAny: ["employee:read", "manager:read"] },
                    { to: "/leave/calendar", label: "Team Calendar", icon: Calendar, permAny: ["employee:read", "manager:read"] },
                    { to: "/leave/workspace", label: "Manager Leave Workspace", icon: UsersThree, permAny: ["employee:read", "manager:read"] },
                ],
            },
            {
                id: "wf-leave-admin",
                label: "Leave Administration",
                icon: Lock,
                items: [
                    // Iter 102.1 — "Configuration" → "Leave Configuration" (prefix).
                    { to: "/admin/leave/configuration", label: "Leave Configuration", icon: Clock, perm: "employee:read" },
                    // Iter 107 · Phase 1 — T-4 HR route elevation (frozen
                    // Decision Log v3): 5 admin-scoped Leave surfaces
                    // promoted from HIDDEN_ROUTES to first-class sidebar.
                    // All were previously discoverable only via global search.
                    { to: "/admin/leave/holiday-calendars", label: "Holiday Calendars", icon: Calendar, perm: "employee:read" },
                    { to: "/admin/leave/balances", label: "Leave Balances (Admin)", icon: Scales, perm: "employee:read" },
                    { to: "/admin/leave/ledger", label: "Leave Ledger Explorer", icon: ListChecks, perm: "employee:read" },
                    { to: "/admin/leave/accrual", label: "Leave Accrual (Operator)", icon: Coins, perm: "employee:read" },
                    { to: "/admin/leave/carry-forward", label: "Leave Carry-Forward", icon: ArrowsClockwise, perm: "employee:read" },
                ],
            },
            {
                // Iter 102.1 — L2 "Insights" → "Workforce Insights" (context).
                id: "wf-insights",
                label: "Workforce Insights",
                icon: ChartLineUp,
                items: [
                    { to: "/workforce/desktop-activity", label: "Desktop Presence", icon: Desktop, perm: "employee:read" },
                    // Iter 99 — Manager's operational home (real-time team health).
                    { to: "/manager/health", label: "Team Health", icon: Pulse, perm: "manager:read" },
                    { to: "/manager/team-activity", label: "Team Activity", icon: UsersThree, perm: "manager:read" },
                    { to: "/admin/workforce/analytics", label: "Workforce Analytics", icon: ChartPieSlice, perm: "workforce:read" },
                ],
            },
            {
                // Interim flat links per operator directive (Stage 1 answer #2).
                // Stage 2A · FPR-024–028 expand Attendance Reports with the
                // 5 canonical report views.
                // Iter 102.1 — All Attendance Reports L3 items contextualised
                // with "Attendance" prefix for consistency.
                id: "wf-attendance-reports",
                label: "Attendance Reports",
                icon: ChartBar,
                items: [
                    { to: "/admin/attendance/reports/monthly-register", label: "Monthly Attendance Register", icon: ChartBar, perm: "employee:read" },
                    { to: "/admin/attendance/reports/daily-summary", label: "Daily Attendance Summary", icon: ChartBar, perm: "employee:read" },
                    { to: "/admin/attendance/reports/monthly-summary", label: "Monthly Attendance Summary", icon: ChartBar, perm: "employee:read" },
                    { to: "/admin/attendance/reports/anomalies", label: "Attendance Exceptions Report", icon: Warning, perm: "employee:read" },
                    { to: "/admin/attendance/reports/regularization", label: "Attendance Regularization Report", icon: FileText, perm: "employee:read" },
                    { to: "/admin/attendance/reports/trends", label: "Attendance Trends", icon: ChartLineUp, perm: "employee:read" },
                ],
            },
            {
                id: "wf-leave-reports",
                label: "Leave Reports",
                icon: Scales,
                items: [
                    // Iter 102.1 — "Balance" → "Leave Balance Report".
                    { to: "/leave/reports/balance", label: "Leave Balance Report", icon: Scales, perm: "employee:read" },
                ],
            },
            {
                // Iter 102.1 — L2 "Agents" → "Desktop Agent";
                // L3 "Agent Registrations" → "Desktop Agent Enrolments".
                id: "wf-agents",
                label: "Desktop Agent",
                icon: Plug,
                items: [
                    { to: "/admin/desktop-agent/registrations", label: "Desktop Agent Enrolments", icon: Plug, perm: "workforce:agent_admin" },
                ],
            },
        ],
    },
    // 5 — EMPLOYEES (Directory · Onboarding · My Workspace)
    {
        id: "employees",
        label: "Employees",
        icon: UsersThree,
        accent: "orange",
        items: [
            { to: "/employees", label: "Directory", icon: AddressBook, perm: "employee:read" },
        ],
        subgroups: [
            {
                id: "em-onboarding",
                label: "Onboarding",
                icon: UsersThree,
                items: [
                    // Iter 86.16 / Phase E7 Phase C — Unified Employee + User onboarding.
                    // Iter 102.1 — "Onboard Employee/User" → "Onboard Employee"
                    // (drop implementation-detail "/User" suffix).
                    { to: "/admin/onboard", label: "Onboard Employee", icon: UsersThree, perm: "employee:onboard" },
                ],
            },
            {
                // ESS / self-view surfaces are visible to all authenticated users —
                // server scopes data to the calling user only.
                id: "my-workspace",
                label: "My Workspace",
                icon: IdentificationCard,
                items: [
                    // Iter 45.6.2 (2026-07-14) · ESS Navigation Visibility
                    // Framework · Phase 2. Model split into two tiers:
                    //   MANDATORY (linkedEmployee gate) — visible to any
                    //     user with a linked employee record. Not
                    //     role-configurable. Fixes the Iter 45.6.1 bug
                    //     where custom roles (e.g. Developer) lost ESS
                    //     visibility because a one-shot migration hadn't
                    //     run in production.
                    //   CONFIGURABLE (perm gate) — Comp-Off + Payslips.
                    //     Administration explicitly enables via Role
                    //     Management. Default: super_admin only.
                    // My Profile remains outside the framework — always
                    // visible to every authenticated user.
                    { to: "/ess/me", label: "My Profile", icon: IdentificationCard },
                    // Iter 2026-02 · Cross-role assignment (2026-02-16).
                    // Any authenticated user can visit; the page will show
                    // an empty state if they haven't been assigned to any
                    // project yet. Kept visible to everyone (no perm gate)
                    // so a Developer / Support person doesn't hunt for
                    // where their assignments went.
                    { to: "/my-projects", label: "My Projects", icon: Briefcase },
                    { to: "/attendance/me", label: "My Attendance", icon: Clock, linkedEmployee: true },
                    { to: "/attendance/my-month", label: "My Attendance & Regularisation", icon: CalendarPlus, linkedEmployee: true },
                    { to: "/leave/portal", label: "My Leave", icon: CalendarPlus, linkedEmployee: true },
                    { to: "/leave/comp-off", label: "My Comp-Off", icon: CalendarPlus, perm: "ess:compoff:view" },
                    { to: "/regularization/my", label: "My Regularizations", icon: FileText, linkedEmployee: true },
                    { to: "/ess/me/payslips", label: "My Payslips", icon: Money, perm: "ess:payslips:view" },
                    { to: "/ess/my-activity", label: "My Activity", icon: ChartLineUp, linkedEmployee: true },
                    { to: "/ess/my-desktop-activity", label: "My Desktop Presence", icon: Desktop, linkedEmployee: true },
                    // W3-A · B9 (2026-08-04) — Universal feedback channel.
                    // Visible to every authenticated user regardless of
                    // role or module chokepoint; admins additionally get
                    // the triage surface further down.
                    { to: "/workspace/suggestions", label: "My Suggestions", icon: Lightbulb },
                    { to: "/workspace/suggestions/admin", label: "Improvement Suggestions (Admin)", icon: Lightbulb, role: "super_admin" },
                ],
            },
        ],
    },
    // 6 — PAYROLL (Payroll Ops · Setup · Modules · Compliance · Reports)
    {
        id: "payroll",
        label: "Payroll",
        icon: Coins,
        accent: "pink",
        items: [],
        subgroups: [
            {
                // Iter 102.1 Nav Cleanup — L2 "Payroll Ops" → "Payroll Operations"
                // (spell out abbreviation).
                //
                // Iter 111.1 UAT (12 Jul 2026): "Salary Assignment" moved
                // from Payroll Operations → Payroll Setup so the three
                // atomic setup steps (Components → Structures → Assignment)
                // live in a single logical group.
                id: "py-ops",
                label: "Payroll Operations",
                icon: Calculator,
                items: [
                    { to: "/admin/payroll/runs", label: "Run Payroll", icon: Calendar, perm: "payroll:read" },
                    { to: "/admin/payroll/period-close", label: "Period Close", icon: Lock, perm: "payroll:read" },
                    { to: "/admin/attendance/payroll-adjustments", label: "Attendance Arrears & Recoveries", icon: Money, permAny: ["payroll:admin", "payroll:approve"] },
                    // Iter 102.1 — "Webhook Events" → "Payroll Event Log" (drop dev jargon).
                    { to: "/admin/payroll/webhooks", label: "Payroll Event Log", icon: Lightning, perm: "payroll:read" },
                    { to: "/admin/gl/exports", label: "GL Posting", icon: ListChecks, perm: "gl:read" },
                ],
            },
            {
                // Iter 102.1 — L2 "Setup" → "Payroll Setup"; L3 "Components"
                // → "Salary Components" (both contextualise).
                //
                // Iter 111.1 UAT (12 Jul 2026): Salary Structures added as
                // the middle step (existing route /admin/payroll/structures,
                // previously discoverable only via HIDDEN_ROUTES + Global
                // Search). Salary Assignment relocated from Payroll
                // Operations for grouping coherence.
                id: "py-setup",
                label: "Payroll Setup",
                icon: Stack,
                items: [
                    { to: "/admin/payroll/components", label: "Salary Components", icon: Coins, perm: "payroll:read" },
                    { to: "/admin/payroll/structures", label: "Salary Structures", icon: Stack, perm: "payroll:read" },
                    { to: "/admin/payroll/assignments", label: "Salary Assignment", icon: AddressBook, perm: "payroll:read" },
                ],
            },
            {
                // Iter 102.1 — L2 "Modules" → "Employee Programs" per operator.
                id: "py-modules",
                label: "Employee Programs",
                icon: Stack,
                items: [
                    { to: "/admin/loans", label: "Loans & Advances", icon: Bank, perm: "loans:read" },
                    { to: "/admin/loans/policies", label: "Loan Policies", icon: Bank, perm: "loans:read" },
                    { to: "/admin/assets", label: "Assets", icon: Briefcase, perm: "assets:read" },
                    { to: "/admin/exit", label: "Exit & FNF", icon: UserMinus, perm: "exit:read" },
                ],
            },
            {
                // Iter 102.1 — L2 "Compliance" → "Payroll Compliance"
                // (contextualise); L3 "Statutory Compliance" → "Compliance
                // Dashboard" (avoid collision + describe the actual page).
                id: "py-compliance",
                label: "Payroll Compliance",
                icon: ShieldCheck,
                items: [
                    { to: "/admin/compliance/health", label: "Compliance Dashboard", icon: ShieldCheck, perm: "compliance:read" },
                    { to: "/admin/compliance/tds-challans", label: "TDS Challans", icon: Receipt, perm: "compliance:read" },
                    { to: "/admin/compliance/tds-returns", label: "TDS Returns", icon: FileText, perm: "compliance:read" },
                    { to: "/admin/compliance/tds-reconciliation", label: "TDS Reconciliation", icon: ArrowsClockwise, perm: "compliance:read" },
                ],
            },
            {
                id: "py-reports",
                label: "Reports",
                icon: ChartLineUp,
                items: [
                    // Iter 102.1 — "Operations Dashboard" → "Payroll Operations Dashboard".
                    { to: "/admin/payroll/dashboards/operations", label: "Payroll Operations Dashboard", icon: ChartLineUp, perm: "payroll:read" },
                ],
            },
        ],
    },
    // 7 — DATA HUB (Repository · Enrichment · Governance)
    {
        id: "data",
        label: "Data Hub",
        icon: Database,
        accent: "cyan",
        items: [],
        subgroups: [
            {
                id: "dh-repository",
                label: "Repository",
                icon: Buildings,
                items: [
                    { to: "/data/companies", label: "Companies", icon: Buildings, perm: "data:read" },
                    // Iter 102.1 — "Coverage" → "Coverage Report" (disambiguation).
                    { to: "/data/coverage", label: "Coverage Report", icon: ChartPieSlice, perm: "data:read" },
                ],
            },
            {
                id: "dh-enrichment",
                label: "Enrichment",
                icon: MagicWand,
                items: [
                    // Iter 102.1 — sidebar perm aligned to route (`data:read`)
                    // per Gap G-2 · was `data:enrich:dry`. Live mode still gated
                    // to PSA server-side; no functional change for real personas.
                    //
                    // Iter 107 · Phase 1 — AP-10 leak fix (frozen Decision Log
                    // v12): Enrichment L2 leaked to `data_sales` via `data:read`.
                    // Data Sales *consumes* the Repository; it does not enrich
                    // it. Hidden from `data_sales` at the sidebar until the
                    // capability-driven `data:enrich:admin` gate ships in RBAC
                    // redesign (candidate DS-1).
                    { to: "/data/logo-enrichment", label: "Logo Enrichment", icon: MagicWand, perm: "data:read", hideForRoles: ["data_sales"] },
                    // Iter 102.1 — "Runs" → "Enrichment Runs" (disambiguation).
                    { to: "/data/logo-enrichment/runs", label: "Enrichment Runs", icon: ChartBar, perm: "data:read", hideForRoles: ["data_sales"] },
                ],
            },
            {
                // Iter 102.1 — L2 "Governance" → "Data Governance" (contextualise);
                // L3 "Verification Console" → "Data Verification" (drop console jargon).
                id: "dh-governance",
                label: "Data Governance",
                icon: ShieldCheck,
                items: [
                    { to: "/admin/verification", label: "Data Verification", icon: ShieldCheck, perm: "data:verify" },
                ],
            },
        ],
    },
    // 8 — ADMINISTRATION (Security · Audit · Governance)
    //
    // Iter 112 · Role Management Blueprint v2.3 Phase 4 (2026-07-14):
    // Legacy "Permission Engine" subgroup removed. The retired PDE
    // surfaces (Responsibilities · Dependency Matrix · Rules ·
    // Validation Report · Permission Migration) have been folded into
    // the consolidated Role Management module (Roles + Role Audit).
    // All previous URLs redirect to /admin/roles at the router level
    // (see App.js) so operator bookmarks continue to land somewhere
    // meaningful.
    {
        id: "admin",
        label: "Administration",
        icon: ShieldCheck,
        accent: "violet",
        items: [],
        subgroups: [
            {
                id: "ad-security",
                label: "Security",
                icon: UsersThree,
                items: [
                    { to: "/users", label: "Users", icon: UsersThree, perm: "user:write" },
                    // Iter 86.15 / Phase E7 — Role + Permission Master.
                    { to: "/admin/roles", label: "Roles", icon: ShieldCheck, perm: "role:read" },
                ],
            },
            {
                id: "ad-audit",
                label: "Audit",
                icon: ShieldCheck,
                items: [
                    // Stage 1 renames:
                    //   /admin/audit-log : "Role Audit Log" → "Role Audit"
                    //   /audit-log       : "Audit Log"      → "System Audit"
                    { to: "/admin/audit-log", label: "Role Audit", icon: ShieldCheck, perm: "role:read" },
                    { to: "/audit-log", label: "System Audit", icon: ShieldCheck, perm: "audit:read" },
                ],
            },
            {
                // Iter 102.4 · Batch 4 · Phase 3 — Cross-module Administrative
                // Actions log surface. "Governance" here is the AUDIT surface
                // for administrative overrides across every module (attendance
                // today; leave/payroll/claims/assets as they adopt AAS).
                //
                // Explicitly NON-overlapping with the "Data Governance" L2
                // below (Batch-4 polish item #7 confirmation):
                //   • Governance         → who overrode what/when/why
                //                          (administrative_actions rows)
                //   • Data Governance    → raw data extraction/export
                //                          (Data Export for compliance)
                //
                // Different collections, different personas, different intent.
                id: "ad-aa-governance",
                label: "Governance",
                icon: Gavel,
                items: [
                    { to: "/admin/administrative-actions", label: "Administrative Actions", icon: Gavel, perm: "attendance:admin:action" },
                ],
            },
            {
                // Iter 102.1 — L2 "Governance" → "Data Governance" per operator.
                // NOTE: same base label ("Governance") as the AA log entry
                // above; disambiguated by the "Data" qualifier since this L2
                // owns raw-data export/extraction (compliance-oriented) while
                // the sibling above owns the administrative-actions audit
                // surface (operator-actions-oriented).
                id: "ad-governance",
                label: "Data Governance",
                icon: Scales,
                items: [
                    { to: "/data-export", label: "Data Export", icon: Database, perm: "user:write" },
                ],
            },
        ],
    },
    // 9 — PLATFORM SETUP (Templates · Diagnostics · Platform · HR)
    {
        id: "platform_setup",
        label: "Platform Setup",
        icon: Wrench,
        accent: "slate",
        items: [],
        subgroups: [
            {
                id: "ps-templates",
                label: "Templates",
                icon: FileDoc,
                items: [
                    { to: "/terms-conditions", label: "Terms & Conditions", icon: Scales, perm: "template:write" },
                    // 2026-02 · NDA Phase 3 · Admin Master screens (gated on their
                    // own capability tokens; hidden from non-admin operators).
                    { to: "/admin/nda-items", label: "NDA Items", icon: Shield, perm: "nda_master:write" },
                    { to: "/admin/nda-templates", label: "NDA Templates", icon: Shield, perm: "nda_template:write" },
                    // Iter 107 · Phase 1 — T-3 Duplicate Product Catalogue
                    // removed (frozen Decision Log v2/v6). The canonical
                    // entry lives under Sales → Proposal Management. Menu
                    // Philosophy: an item belongs to one persona-owned L1;
                    // duplication under Platform Setup created "why am I
                    // seeing this?" ambiguity for Sales · Viewer · Super
                    // Admin. Removed here.
                    // Iter 102.1 — HR Templates perm `template:write` → `employee:read`
                    // per Gap G-4 (align sidebar to route gating).
                    { to: "/admin/hr-templates", label: "HR Templates", icon: IdentificationCard, perm: "employee:read" },
                ],
            },
            {
                id: "ps-diagnostics",
                label: "Diagnostics",
                icon: Pulse,
                items: [
                    { to: "/admin/attendance-diagnostic", label: "Attendance Diagnostic", icon: Pulse, primary: true },
                    { to: "/admin/email-diagnostic", label: "Email Diagnostic", icon: Envelope, primary: true },
                ],
            },
            {
                // Iter 102.1 — L2 "Platform" → "System Settings" per operator.
                // All 3 L3 labels also contextualised.
                id: "ps-platform",
                label: "System Settings",
                icon: Lightning,
                items: [
                    { to: "/admin/module-codes", label: "Proposal Module Codes", icon: Stack, primary: true },
                    { to: "/admin/migration-mode", label: "Tenant Migration Mode", icon: Database, primary: true },
                    // Iter 111.1 — Super Admin Override: time-boxed backdate bypass sessions.
                    // Primary Super Admin only. Frontend hides via `primary: true`; backend
                    // enforces via `require_primary_super_admin` on every write endpoint.
                    { to: "/admin/super-admin-override", label: "Super Admin Override", icon: ShieldCheck, primary: true },
                    { to: "/settings/pipeline-digest", label: "Sales Pipeline Digest", icon: Envelope, perm: "user:write" },
                ],
            },
            {
                // Iter 102.1 — L2 "HR" → "HR Configuration" (context);
                // L3 "HR Setup" → "HR Masters"; "HR Acceptance" → "Document Acceptance".
                id: "ps-hr",
                label: "HR Configuration",
                icon: IdentificationCard,
                items: [
                    { to: "/admin/hr-masters", label: "HR Masters", icon: IdentificationCard, perm: "employee:read" },
                    { to: "/admin/hr-acceptance-dashboard", label: "Document Acceptance", icon: ChartBar, perm: "employee:read" },
                ],
            },
            {
                // UAT 2026-07-28 · Defect 2 sidebar-visibility fix.
                // Work Role, Business Unit and Cost Centre master pages
                // existed as routes since Phase E4 but had no sidebar
                // link anywhere — operators could only reach them by
                // typing the URL directly. Grouped here under Platform
                // Setup because these are organisational-structure
                // masters (parallels HR Masters and Vendor Directory,
                // both persona-owned by the platform administrator).
                // Each item's per-page RequirePerm gate is preserved
                // untouched — the sidebar entry is a pure visibility
                // wrapper.
                id: "ps-org-masters",
                label: "Organisation Masters",
                icon: Buildings,
                items: [
                    { to: "/admin/business-unit-master", label: "Business Units", icon: Buildings, perm: "bu_master:read" },
                    { to: "/admin/cost-centre-master", label: "Cost Centres", icon: Coins, perm: "cost_centre_master:read" },
                    { to: "/admin/work-role-master", label: "Work Roles", icon: IdentificationCard, perm: "work_role_master:read" },
                ],
            },
        ],
    },
];

// ── Path → owner helpers ─────────────────────────────────────────────
// Iter 101 · Stage 1.5 — Given a route path, return the { l1Id, l2Id }
// pair owning that path. l1Id is the top-level module; l2Id is the
// subgroup (or null if the item lives at the module's root items[]
// array). Used by deep-link auto-expansion and initial state seeding.
export function findOwnersForPath(path) {
    for (const g of NAV_GROUPS) {
        if ((g.items || []).some((i) => path.startsWith(i.to))) {
            return { l1Id: g.id, l2Id: null };
        }
        for (const sg of (g.subgroups || [])) {
            if ((sg.items || []).some((i) => path.startsWith(i.to))) {
                return { l1Id: g.id, l2Id: sg.id };
            }
        }
    }
    return { l1Id: null, l2Id: null };
}

// Backwards-compatible L1-only wrapper — used by GroupAccordion +
// GroupRail for active-state highlighting on the module header.
export function findGroupForPath(path) {
    return findOwnersForPath(path).l1Id;
}

// ── Iter 103.0 Phase 2 · Search-only hidden orphan routes ──────────
// Routes that live in App.js with a valid `RequirePerm` gate + page
// component but are NOT wired into the sidebar today. Surfacing them
// via the global search dialog closes the discoverability gap without
// mutating the sidebar IA (per REEDOS Iter 103.0 approval 2026-02-11:
// "Sidebar IA remains unchanged in this iteration").
//
// Each entry declares:
//   • to       — URL path (must match App.js route exactly)
//   • label    — display label in the search dialog
//   • icon     — Phosphor icon (matches sidebar convention)
//   • perm     — permission gate (mirrors App.js `RequirePerm`)
//   • groupId  — parent NAV_GROUP id for breadcrumb assembly
//   • subgroupId — parent subgroup id for breadcrumb assembly
//   • hidden   — always true; kept explicit for readability
//
// GOVERNANCE — Iter 103.0 excluded routes:
//   • /leave/reports/ledger is DELIBERATELY EXCLUDED per REEDOS
//     directive: "Do not expose a known 500 page through search"
//     (PFD-1 known defect, deferred backlog). Log it separately as
//     a blocked discoverability item — will be added back once
//     PFD-1 is fixed.
export const HIDDEN_ROUTES = [
    // ── Leave Administration ──
    // Iter 107 · Phase 1 — T-4 HR route elevation: the following 5
    // routes moved to first-class sidebar under Workforce → Leave
    // Administration. They no longer appear in HIDDEN_ROUTES.
    //   /admin/leave/holiday-calendars
    //   /admin/leave/balances
    //   /admin/leave/ledger
    //   /admin/leave/accrual
    //   /admin/leave/carry-forward
    // ── Leave Operations ──
    // Iter 107 · Phase 1 — T-4: "Manager Leave Workspace" elevated to
    // first-class sidebar under Workforce → Leave Operations.
    //   /leave/workspace
    // ── Leave Reports (partial — /leave/reports/ledger EXCLUDED per PFD-1) ──
    { to: "/leave/reports/utilization",         label: "Leave Utilization Report", icon: ChartBar, perm: "employee:read", groupId: "workforce", subgroupId: "wf-leave-reports" },
    { to: "/leave/reports/pending-approval",    label: "Leave Pending Approval Report", icon: FileText, perm: "employee:read", groupId: "workforce", subgroupId: "wf-leave-reports" },
    // ── Attendance (admin internals) ──
    { to: "/admin/attendance/events",       label: "Attendance Events Console", icon: Lightning, perm: "employee:read", groupId: "workforce", subgroupId: "wf-attendance" },
    { to: "/admin/attendance/snapshots",    label: "Attendance Snapshots", icon: Database, perm: "employee:read", groupId: "workforce", subgroupId: "wf-attendance" },
    // ── ESS-adjacent ──
    { to: "/attendance/portal",             label: "Attendance Employee Portal", icon: Clock, perm: "employee:read", groupId: "employees", subgroupId: "my-workspace" },
    // ── Payroll Setup / Compliance ──
    // Iter 111.1 UAT (12 Jul 2026): "Salary Structures" was previously
    // a hidden orphan discoverable only via Global Search — promoted to
    // first-class L3 under Payroll Setup (see NAV_GROUPS above). Entry
    // removed from HIDDEN_ROUTES to avoid duplicate search results.
    { to: "/admin/payroll/compliance",      label: "Payroll Compliance", icon: ShieldCheck, perm: "payroll:read", groupId: "payroll", subgroupId: "py-compliance" },
    { to: "/admin/payroll/compliance/pt-master", label: "Professional Tax (PT) Master", icon: ShieldCheck, perm: "payroll:read", groupId: "payroll", subgroupId: "py-compliance" },
    { to: "/admin/compliance/console",      label: "Compliance Console", icon: ShieldCheck, perm: "compliance:read", groupId: "payroll", subgroupId: "py-compliance" },
    { to: "/admin/compliance/bonus",        label: "Statutory Bonus Console", icon: HandCoins, perm: "compliance:read", groupId: "payroll", subgroupId: "py-compliance" },
    { to: "/admin/compliance/gratuity-audit", label: "Gratuity Audit Console", icon: ShieldCheck, perm: "compliance:read", groupId: "payroll", subgroupId: "py-compliance" },
];

// ── Iter 103.0 Phase 2 · Flatten helper for menu search ────────────
// Walks NAV_GROUPS + DASHBOARD_ITEM + HIDDEN_ROUTES and returns a
// FLAT array of only-visible items with a `breadcrumb` array attached
// for search-result display. RBAC filtering reuses `isItemVisible()`
// verbatim — same gate as the sidebar. Zero new authorization logic.
//
// Output shape (per item):
//   {
//     id:          stable string derived from route path (e.g. "admin-attendance-shifts")
//     label:       "Shifts"
//     breadcrumb:  ["Workforce", "Attendance", "Shifts"]   (excluding the label itself is fine either way; we include it for display symmetry)
//     href:        "/admin/attendance/shifts"
//     icon:        Phosphor icon component
//     group:       "workforce"        (L1 id — used for accent color)
//     subgroup:    "wf-attendance"    (L2 id)
//     hidden:      false | true       (true for HIDDEN_ROUTES orphans)
//     kind:        "nav"              (extension hook — future values: "action" | "recent" | "favourite")
//   }
export function flattenNavItems(user, can) {
    const flat = [];
    const seen = new Set(); // Iter 103.0 Phase 2 · deduplicate by href.
    // Some routes (F.8 multi-entry: `/catalogue`, `/terms-conditions`)
    // are intentionally listed twice in NAV_GROUPS so operators can
    // discover them from either the Sales module or Platform Setup.
    // In sidebar rendering this is fine — each entry lives under a
    // different breadcrumb + accordion. In flat search results the
    // duplication both (a) collides React keys (routeToId is href-
    // based → same slug) and (b) surfaces duplicate rows to users.
    // We keep the FIRST occurrence (the semantically-primary
    // breadcrumb) and drop later dupes.
    const push = (entry) => {
        if (seen.has(entry.href)) return;
        seen.add(entry.href);
        flat.push(entry);
    };

    // 1. Dashboard root entry (no group).
    if (isItemVisible(DASHBOARD_ITEM, user, can)) {
        push({
            id: routeToId(DASHBOARD_ITEM.to),
            label: DASHBOARD_ITEM.label,
            breadcrumb: [DASHBOARD_ITEM.label],
            href: DASHBOARD_ITEM.to,
            icon: DASHBOARD_ITEM.icon,
            group: null,
            subgroup: null,
            hidden: false,
            kind: "nav",
        });
    }

    // 2. Walk NAV_GROUPS → items + subgroups.
    for (const g of NAV_GROUPS) {
        // L1 group's own items[] (root-level items inside a module,
        // e.g. Employees → Directory).
        for (const item of (g.items || [])) {
            if (!isItemVisible(item, user, can)) continue;
            push({
                id: routeToId(item.to),
                label: item.label,
                breadcrumb: [g.label, item.label],
                href: item.to,
                icon: item.icon,
                group: g.id,
                subgroup: null,
                hidden: false,
                kind: "nav",
            });
        }
        // L2 subgroup items.
        for (const sg of (g.subgroups || [])) {
            for (const item of (sg.items || [])) {
                if (!isItemVisible(item, user, can)) continue;
                push({
                    id: routeToId(item.to),
                    label: item.label,
                    breadcrumb: [g.label, sg.label, item.label],
                    href: item.to,
                    icon: item.icon,
                    group: g.id,
                    subgroup: sg.id,
                    hidden: false,
                    kind: "nav",
                });
            }
        }
    }

    // 3. Hidden orphan routes — RBAC via same isItemVisible contract.
    for (const orphan of HIDDEN_ROUTES) {
        if (!isItemVisible(orphan, user, can)) continue;
        // Resolve breadcrumb from groupId + subgroupId.
        const g = NAV_GROUPS.find((x) => x.id === orphan.groupId);
        const sg = g && (g.subgroups || []).find((x) => x.id === orphan.subgroupId);
        const crumb = [
            g?.label,
            sg?.label,
            orphan.label,
        ].filter(Boolean);
        push({
            id: routeToId(orphan.to),
            label: orphan.label,
            breadcrumb: crumb.length ? crumb : [orphan.label],
            href: orphan.to,
            icon: orphan.icon,
            group: orphan.groupId || null,
            subgroup: orphan.subgroupId || null,
            hidden: true,
            kind: "nav",
        });
    }

    return flat;
}

// Route → stable id. Deterministic, safe for React keys + testids.
function routeToId(path) {
    return path
        .replace(/^\/+/, "")
        .replace(/\/+$/, "")
        .replace(/[^a-zA-Z0-9]+/g, "-")
        .toLowerCase() || "root";
}
